import { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    Card,
    CardContent,
} from "@component/card";
import { useParams } from "react-router-dom";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { getAssessmentCompanyDemo } from "@service/assessment";
import { DemoCompanyInfo } from "@type/company";
import BasicLayout from "@container/layout/basic";
import { blackListDomain } from "@constant/domina";
import { triggerOutboundCall } from "@service/interview";
import CountryCodeSelect from "@component/country-code-select";
import { COUNTRY_CODES, CountryCode } from "@component/country-codes-data";

export default function AIInterviewDemoCall() {
    const { companyname } = useParams()
    const [isStarting, setIsStarting] = useState(false);
    const [isCallTriggered, setIsCallTriggered] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        yourName: "",
        email: "",
        phoneNumber: "",
    });
    const [companyInfo, setCompanyInfo] = useState<DemoCompanyInfo | undefined>(undefined);
    const [selectedCountry, setSelectedCountry] = useState<CountryCode>(
        COUNTRY_CODES.find((c) => c.code === "+60") || COUNTRY_CODES[0]
    );

    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    setIsCallTriggered(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [countdown]);

    useEffect(() => {
        if (companyname) {
            getAssessmentCompanyDemo(companyname).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);;
                }
            })
        }
    }, [companyname])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Check if all form fields are filled
    const areAllFieldsFilled = () => {
        return formData.yourName.trim() !== "" && formData.email.trim() !== "" && formData.phoneNumber.trim() !== "";
    };

    const handleStartInterview = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Only proceed if all fields are filled
        if (!areAllFieldsFilled()) return;

        const isBlacklisted = blackListDomain.some((domain) =>
            formData.email.toLowerCase().endsWith(domain)
        );
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
        if (isBlacklisted) {
            setError(labels.emailError);
            return
        } else if (!isValidEmail) {
            setError(labels.invalidEmailError);
            return
        }

        try {
            setIsStarting(true);
            setError(null);

            // Trigger outbound phone call with country code
            const response = await triggerOutboundCall(`${selectedCountry.code}${formData.phoneNumber}`, formData.yourName, formData.email);

            if (response.status !== 200) {
                setError(isIndonesian ? "Gagal melakukan panggilan. Silakan coba lagi." : "Failed to initiate the call. Please try again.");
                setIsStarting(false);
                return;
            }

            setIsCallTriggered(true);
            setCountdown(180);
            setIsStarting(false);
        } catch (error) {
            setError(isIndonesian ? "Gagal melakukan panggilan. Silakan coba lagi." : "Failed to initiate the call. Please try again.");
            console.error("Error triggering outbound call:", error);
            setIsStarting(false);
        }
    };

    const isFormValid = formData.yourName.trim() !== "" && formData.email.trim() !== "" && formData.phoneNumber.trim() !== "";

    // Check if we need Indonesian translation
    const isIndonesian = companyname === "kopi-kenangan";

    // Translation labels
    const labels = {
        title: isIndonesian ? "Demo Wawancara AI" : "AI Interviewer Demo",
        subtitle: isIndonesian
            ? `Demo khusus untuk ${companyInfo?.company_name || ""}.`
            : `A personalized demo call for ${companyInfo?.company_name || ""}.`,
        description: isIndonesian
            ? "Demo ini menunjukkan cara kerja Wawancara AI ourteam."
            : "This demo showcases how ourteam's AI Interview works.",
        yourNameLabel: isIndonesian ? "Nama Lengkap" : "Your Name",
        yourNamePlaceholder: isIndonesian ? "Masukkan nama lengkap Anda" : "Your name",
        emailLabel: "Email",
        emailPlaceholder: isIndonesian ? "nama@perusahaan.com" : "your.email@example.com",
        phoneNumberLabel: isIndonesian ? "Nomor Telepon" : "Phone Number",
        phoneNumberPlaceholder: isIndonesian ? "Masukkan nomor telepon Anda" : "Your phone number",
        privacyText: isIndonesian
            ? "Setelah submit, Anda akan menerima panggilan telepon. Jika tidak masuk, silakan cek panggilan masuk dan pastikan nomor telepon Anda tidak dinonaktifkan."
            : <>
                You will receive an AI demo call immediately after submitting. <br />
                Be ready to answer your phone. Ensure your device can receive incoming calls and is not on <b>silent</b> or <b>Do Not Disturb</b> mode. <br /> <br />
                If the call does not come through, please check your number and try again.
            </>,
        contactText: isIndonesian ? "Untuk pertanyaan lebih lanjut, hubungi" : "For enquiries, contact",
        proceedButton: isIndonesian ? "Mulai" : "Proceed",
        callTriggered: isIndonesian ? "Panggilan sedang berlangsung..." : "Call in progress...",
        preparingCall: isIndonesian ? "Sedang mempersiapkan..." : "Preparing Call...",
        emailError: isIndonesian ? "Mohon gunakan email perusahaan." : "Please enter company email address.",
        invalidEmailError: isIndonesian ? "Format email tidak valid." : "Please enter a valid email address.",
        startError: isIndonesian ? "Gagal memulai wawancara. Silakan coba lagi." : "Failed to start the interview. Please try again.",
    };

    return (
        <BasicLayout
            hideNavbarMenu
            disableMinHeight
            customLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
            cssRoot="!pb-0"
        >
            <div className="text-center z-10 w-full">
                <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 w-full py-12 px-4 rounded-b-[3rem] shadow-2xl border-b border-gray-700 pt-0 pb-72 gap-8">
                    <div className="flex justify-center mb-0 pt-10">
                        <img
                            src={companyInfo?.company_logo || ""}
                            alt={companyInfo?.company_name || ""}
                            className="h-16 w-auto object-contain"
                        />
                    </div>
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight whitespace-nowrap bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                        {labels.title}
                    </h1>
                    {/* Subtitle moved from form component */}
                    <div className="flex items-center justify-center mt-2">
                        <span className="text-base text-gray-300 font-medium tracking-wide pt-0">
                            {labels.subtitle}
                        </span>
                    </div>
                </div>
            </div>

            <div className="absolute z-20 flex justify-center w-full px-4 transform transition-all duration-300 top-[200px] sm:top-[260px] bg-transparent">
                <div className="w-full max-w-xl">
                    {/* Main content card */}
                    <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border border-gray-200/50 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1">
                        <CardContent
                            className="pt-3 pb-6 px-6 bg-white"
                            style={{
                                borderRadius: "1rem",
                                paddingRight: "22px",
                                paddingLeft: "22px",
                                paddingTop: "24px",
                            }}
                        >
                            {/* Description */}
                            <p
                                className="text-sm text-gray-600 text-center mb-6 leading-relaxed font-medium"
                            >
                                {labels.description}
                            </p>

                            {/* Form */}
                            <form onSubmit={handleStartInterview} className="space-y-5">
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="yourName"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        {labels.yourNameLabel}
                                    </Label>
                                    <Input
                                        id="yourName"
                                        type="text"
                                        name="yourName"
                                        placeholder={labels.yourNamePlaceholder}
                                        value={formData.yourName}
                                        onChange={handleInputChange}
                                        className="h-11 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                        required
                                        disabled={isCallTriggered}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="email"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        {labels.emailLabel}
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder={labels.emailPlaceholder}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="h-11 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                        required
                                        disabled={isCallTriggered}
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="phoneNumber"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        {labels.phoneNumberLabel}
                                    </Label>
                                    <div className="flex">
                                        <CountryCodeSelect
                                            value={selectedCountry}
                                            onChange={(value) => setSelectedCountry(value)}
                                            height="h-11"
                                            disabled={isCallTriggered}
                                        />
                                        <Input
                                            id="phoneNumber"
                                            type="tel"
                                            name="phoneNumber"
                                            placeholder={labels.phoneNumberPlaceholder}
                                            value={formData.phoneNumber}
                                            onChange={(e) => {
                                                const value = e.target.value.replace(/\D/g, "");
                                                setFormData(prev => ({ ...prev, phoneNumber: value }));
                                            }}
                                            className="h-11 rounded-l-none border-l-0 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                            required
                                            disabled={isCallTriggered}
                                        />
                                    </div>
                                </div>

                                {/* Info text */}
                                <div className="text-center text-sm text-gray-600 leading-relaxed">
                                    <p>
                                        {labels.privacyText}
                                        <br />
                                        {labels.contactText} <span className="underline">hello@ourteam.ai</span>.
                                    </p>
                                </div>

                                {error && (
                                    <div className="text-red-500 mb-4 text-center">
                                        {error}
                                    </div>
                                )}

                                {/* Submit button */}
                                <Button
                                    disabled={isStarting || countdown > 0 || !areAllFieldsFilled()}
                                    className={`w-full h-12 font-medium rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${countdown > 0
                                        ? "bg-green-600 text-white cursor-not-allowed"
                                        : isFormValid
                                            ? "bg-gradient-to-r from-gray-900 to-black text-white hover:from-black hover:to-gray-900 shadow-lg hover:shadow-xl"
                                            : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                >
                                    {isStarting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            {labels.preparingCall}
                                        </>
                                    ) : countdown > 0
                                        ? labels.callTriggered
                                        : labels.proceedButton}
                                </Button>

                                {countdown > 0 && (
                                    <p className="text-sm text-gray-500 text-center mt-2">
                                        {isIndonesian
                                            ? `Coba lagi dalam ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`
                                            : `You can try again in ${Math.floor(countdown / 60)}:${String(countdown % 60).padStart(2, "0")}`}
                                    </p>
                                )}
                            </form>

                            {/* Footer */}
                            <div className="text-center mt-6" style={{ paddingBottom: "16px" }}>
                                <img
                                    src="https://assets.polymet.ai/nervous-tan-828931"
                                    alt="Powered by ourteam"
                                    className="mx-auto h-12 w-auto opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </BasicLayout>
    );
}
